import { Button, Dialog } from "@hisani/ui";
import {
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  FileCheck2,
  Flag,
  Forward,
  Heart,
  Images,
  MapPin,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";
import type { OwnedListing } from "./marketplace-contracts";
import type { MarketplacePreviewListing } from "./marketplace-sample-data";
import {
  breedLabel,
  formatListingPrice,
  formatRegion,
  sexLabel,
} from "./marketplace-formatting";

const listingMediaBadgeClassName =
  "inline-flex min-h-5 max-w-full items-center gap-0.5 whitespace-nowrap rounded-md border bg-surface/75 px-1 text-body-small font-semibold text-primary shadow-sm backdrop-blur-md";

type MarketplaceListingPresentation = Omit<
  MarketplacePreviewListing,
  "status"
> & {
  readonly status: OwnedListing["status"];
};

export function MarketplaceListingCard({
  listing,
  locale,
  pending,
  onOpen,
  onBookmark,
  onShare,
  statusBadge,
  managementActions,
}: {
  readonly listing: MarketplaceListingPresentation;
  readonly locale: string;
  readonly pending: boolean;
  readonly onOpen: () => void;
  readonly onBookmark?: (() => void) | undefined;
  readonly onShare?: (() => void) | undefined;
  readonly statusBadge?: React.ReactNode;
  readonly managementActions?: React.ReactNode;
}) {
  const t = useTranslations("Marketplace");
  return (
    <article className="group relative overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-[var(--elevation-1)] transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-border-default hover:shadow-elevation-2 motion-reduce:hover:translate-y-0">
      <div
        className={`relative overflow-hidden bg-subtle ${listing.photos.length > 1 ? "z-20" : ""}`}
      >
        <ListingPhotoCarousel listing={listing} />
        <div
          className={`absolute start-1.5 top-1.5 z-20 flex flex-wrap gap-0.5 ${onBookmark === undefined && onShare === undefined ? "end-1.5" : "end-24"}`}
        >
          {listing.verifiedOwner ? (
            <span
              className={`${listingMediaBadgeClassName} border-border-subtle`}
            >
              <BadgeCheck
                className="size-3 text-brand-ink"
                aria-hidden="true"
              />
              {t("verifiedOwner")}
            </span>
          ) : null}
          {listing.veterinaryReportSupplied ? (
            <span
              className={`${listingMediaBadgeClassName} border-border-brand text-brand-ink`}
            >
              <FileCheck2 className="size-3" aria-hidden="true" />
              {t("vetReportBadge")}
            </span>
          ) : null}
          {statusBadge}
        </div>
        {onBookmark === undefined && onShare === undefined ? null : (
          <div className="absolute end-1 top-1 z-20 flex flex-col items-center text-action-primary-ink drop-shadow-sm">
            {onBookmark === undefined ? null : (
              <button
                type="button"
                aria-label={
                  listing.bookmarked ? t("removeBookmark") : t("saveBookmark")
                }
                aria-pressed={listing.bookmarked}
                disabled={pending}
                onClick={onBookmark}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
              >
                <Heart
                  className={`size-6 ${listing.bookmarked ? "fill-current" : ""}`}
                  aria-hidden="true"
                />
              </button>
            )}
            {onShare === undefined ? null : (
              <button
                type="button"
                aria-label={t("shareListing")}
                onClick={onShare}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <Forward className="size-6" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="min-w-0 p-3">
        <p className="text-heading-medium text-primary">
          {formatListingPrice(listing, locale, t("pricingContact"))}
        </p>
        <div className="mt-0.5 flex min-w-0 items-baseline gap-1.5">
          <h3 className="text-heading-small min-w-0 truncate text-primary">
            {listing.horseName}
          </h3>
          <span className="text-tertiary" aria-hidden="true">
            ·
          </span>
          <p className="min-w-0 truncate text-body-small text-secondary">
            {breedLabel(listing, t)}
          </p>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="rounded-md bg-subtle px-2 py-0.5 text-body-small text-primary">
            <strong>{t("ageLabel")}:</strong>{" "}
            {t("ageYears", { age: listing.ageYears })}
          </span>
          <span className="rounded-md bg-subtle px-2 py-0.5 text-body-small text-primary">
            <strong>{t("sex")}:</strong> {sexLabel(listing.sex, t)}
          </span>
          <p className="ms-auto flex min-w-0 items-center gap-1 text-body-small text-secondary">
            <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{listing.city}</span>
          </p>
        </div>
      </div>
      {managementActions === undefined ? null : (
        <div className="relative z-20 border-t border-border-subtle p-3">
          {managementActions}
        </div>
      )}
      <button
        type="button"
        aria-label={t("viewListing")}
        onClick={onOpen}
        className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
      />
    </article>
  );
}

export function MarketplaceListingDetailDialog({
  listing,
  locale,
  pending,
  canMessage,
  onClose,
  onBookmark,
  onMessage,
  onReport,
  detailBadge,
}: {
  readonly listing: MarketplaceListingPresentation | null;
  readonly locale: string;
  readonly pending: boolean;
  readonly canMessage: boolean;
  readonly onClose: () => void;
  readonly onBookmark?: (() => void) | undefined;
  readonly onMessage?: (() => void) | undefined;
  readonly onReport?: (() => void) | undefined;
  readonly detailBadge?: React.ReactNode;
}) {
  const t = useTranslations("Marketplace");
  return (
    <Dialog
      open={listing !== null}
      title={listing?.horseName ?? t("listingDetails")}
      {...(listing === null
        ? {}
        : {
            description: `${breedLabel(listing, t)} · ${sexLabel(listing.sex, t)} · ${t("ageYears", { age: listing.ageYears })}`,
          })}
      closeLabel={t("close")}
      mobileFullscreen
      onClose={onClose}
      footer={
        listing === null ||
        (onBookmark === undefined &&
          (onMessage === undefined || !canMessage)) ? undefined : (
          <>
            {onBookmark === undefined ? null : (
              <Button
                type="button"
                variant="secondary"
                onClick={onBookmark}
                disabled={pending}
                className="gap-2"
              >
                <Heart
                  className={`size-4 ${listing.bookmarked ? "fill-current" : ""}`}
                  aria-hidden="true"
                />
                {listing.bookmarked ? t("removeBookmark") : t("saveBookmark")}
              </Button>
            )}
            {canMessage && onMessage !== undefined ? (
              <Button
                type="button"
                variant="accent"
                onClick={onMessage}
                disabled={pending}
                className="gap-2"
              >
                <MessageCircle className="size-4" aria-hidden="true" />
                {t("messageSeller")}
              </Button>
            ) : null}
          </>
        )
      }
    >
      {listing === null ? null : (
        <div className="grid gap-5">
          <div className="relative">
            <ListingPhoto
              listing={listing}
              className="aspect-[4/3] rounded-md"
            />
            <ListingPhotoWatermark />
            <span className="absolute end-3 bottom-3 rounded-full border border-border-subtle bg-surface/95 px-3 py-1.5 text-xs font-bold text-primary shadow-sm">
              {t("photoCount", { count: listing.photos.length })}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-3xl font-bold">
              {formatListingPrice(listing, locale, t("pricingContact"))}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {listing.verifiedOwner ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-brand-subtle px-3 py-2 text-xs font-bold text-brand-ink">
                  <BadgeCheck className="size-4" aria-hidden="true" />
                  {t("verifiedOwner")}
                </span>
              ) : null}
              {detailBadge}
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-4 rounded-md border border-border-subtle bg-subtle p-4">
            <DetailTerm label={t("seller")} value={listing.sellerName} />
            <DetailTerm
              label={t("location")}
              value={`${listing.city}, ${formatRegion(listing.countryCode, locale)}`}
            />
            <DetailTerm label={t("dateOfBirth")} value={listing.dateOfBirth} />
            {listing.sire === null ? null : (
              <DetailTerm label={t("sire")} value={listing.sire} />
            )}
            {listing.dam === null ? null : (
              <DetailTerm label={t("dam")} value={listing.dam} />
            )}
            <DetailTerm
              label={t("updated")}
              value={new Intl.DateTimeFormat(locale, {
                dateStyle: "medium",
              }).format(new Date(listing.updatedAt))}
            />
            <DetailTerm label={t("listingType")} value={t("wholeHorse")} />
          </dl>
          {listing.description === null ? null : (
            <section>
              <h3 className="text-lg font-bold">{t("aboutHorse")}</h3>
              <p className="mt-2 text-sm leading-6 text-secondary">
                {listing.description}
              </p>
            </section>
          )}
          {listing.horseProfileUrl === null ? null : (
            <a
              href={listing.horseProfileUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center text-sm font-semibold text-brand-ink underline underline-offset-4"
            >
              {t("viewHorseProfile")}
            </a>
          )}
          {listing.videos.length === 0 ? null : (
            <section className="grid gap-3" aria-labelledby="listing-videos">
              <h3 id="listing-videos" className="text-lg font-bold">
                {t("videos")}
              </h3>
              {listing.videos.map((video) => (
                <video
                  key={video.id}
                  controls
                  preload="metadata"
                  className="w-full rounded-md border border-border-subtle"
                  src={video.url}
                >
                  {video.filename}
                </video>
              ))}
            </section>
          )}
          <section className="rounded-md border border-border-subtle p-4">
            <h3 className="flex items-center gap-2 text-lg font-bold">
              <FileCheck2
                className="size-5 text-brand-ink"
                aria-hidden="true"
              />
              {t("vetDisclosure")}
            </h3>
            {listing.veterinaryReportSupplied ? (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-subtle px-3 py-1.5 text-xs font-bold text-brand-ink">
                <FileCheck2 className="size-4" aria-hidden="true" />
                {t("vetReportSupplied")}
              </p>
            ) : null}
            <p className="mt-2 text-sm leading-6 text-secondary">
              {listing.vetDisclosureNotes ?? t("noVetNotes")}
            </p>
            {listing.vetDocuments.length === 0 ? null : (
              <ul className="mt-3 grid gap-2">
                {listing.vetDocuments.map((document) => (
                  <li key={document.id}>
                    <a
                      href={document.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-11 items-center text-sm font-semibold text-brand-ink underline decoration-border-brand underline-offset-4"
                    >
                      {document.filename}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <div className="rounded-md border border-brand/40 bg-brand-subtle p-4 text-sm leading-6 text-brand-ink">
            <ShieldCheck className="me-2 inline size-5" aria-hidden="true" />
            {t("offPlatformNotice")}
          </div>
          {onReport === undefined ? null : (
            <Button
              type="button"
              variant="secondary"
              onClick={onReport}
              className="min-h-12 w-full gap-2 text-secondary"
            >
              <Flag className="size-4" aria-hidden="true" />
              {t("reportListing")}
            </Button>
          )}
        </div>
      )}
    </Dialog>
  );
}

function ListingPhotoWatermark() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      {/* HISANI-QA: This is the official transparent brand asset; the overlay is decorative. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/brand/hisani-logo.png"
        alt=""
        className="w-1/3 max-w-32 opacity-40"
      />
    </div>
  );
}

function ListingPhotoCarousel({
  listing,
}: {
  readonly listing: MarketplaceListingPresentation;
}) {
  const t = useTranslations("Marketplace");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const slideRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const dragRef = React.useRef<{
    readonly pointerId: number;
    readonly startScrollLeft: number;
    readonly startX: number;
  } | null>(null);
  const hasMultiplePhotos = listing.photos.length > 1;

  function showPhoto(index: number) {
    const nextIndex = Math.max(0, Math.min(index, listing.photos.length - 1));
    setActiveIndex(nextIndex);
    slideRefs.current[nextIndex]?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  }

  function updateActivePhoto() {
    const scroller = scrollerRef.current;
    if (scroller === null) return;
    const scrollerCenter =
      scroller.getBoundingClientRect().left + scroller.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const [index, slide] of slideRefs.current.entries()) {
      if (slide === null) continue;
      const bounds = slide.getBoundingClientRect();
      const distance = Math.abs(
        bounds.left + bounds.width / 2 - scrollerCenter,
      );
      if (distance < closestDistance) {
        closestIndex = index;
        closestDistance = distance;
      }
    }
    setActiveIndex(closestIndex);
  }

  function finishMouseDrag(event: React.PointerEvent<HTMLDivElement>) {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    dragRef.current = null;
    updateActivePhoto();
  }

  if (!hasMultiplePhotos)
    return (
      <div className="relative">
        <ListingPhoto listing={listing} className="aspect-[2/1]" showBackdrop />
        <ListingPhotoWatermark />
        <PhotoPosition count={listing.photos.length} />
      </div>
    );

  return (
    <div
      role="region"
      aria-label={t("listingPhotos", { horseName: listing.horseName })}
      className="relative"
    >
      <div
        ref={scrollerRef}
        className="flex aspect-[2/1] cursor-grab snap-x snap-mandatory select-none overflow-x-auto overscroll-x-contain touch-pan-x bg-subtle active:cursor-grabbing [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        onScroll={updateActivePhoto}
        onPointerDown={(event) => {
          if (event.pointerType !== "mouse" || event.button !== 0) return;
          dragRef.current = {
            pointerId: event.pointerId,
            startScrollLeft: event.currentTarget.scrollLeft,
            startX: event.clientX,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          if (drag?.pointerId !== event.pointerId) return;
          event.currentTarget.scrollLeft =
            drag.startScrollLeft - (event.clientX - drag.startX);
        }}
        onPointerUp={finishMouseDrag}
        onPointerCancel={finishMouseDrag}
      >
        {listing.photos.map((photo, index) => (
          <div
            key={photo.id}
            ref={(element) => {
              slideRefs.current[index] = element;
            }}
            className="flex h-full w-full shrink-0 snap-start items-center justify-center bg-subtle"
          >
            <ListingPhoto
              listing={listing}
              photo={photo}
              alt={t("listingPhotoAlt", {
                horseName: listing.horseName,
                current: index + 1,
                count: listing.photos.length,
              })}
              className="h-full"
              showBackdrop
            />
          </div>
        ))}
      </div>
      <ListingPhotoWatermark />
      <button
        type="button"
        aria-label={t("previousPhoto")}
        disabled={activeIndex === 0}
        onClick={() => {
          showPhoto(activeIndex - 1);
        }}
        className="absolute start-1 top-1/2 z-20 inline-flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-0"
      >
        <span className="grid size-8 place-items-center rounded-full border border-border-subtle bg-surface/95 shadow-sm backdrop-blur-sm">
          <ChevronLeft className="size-5 rtl:rotate-180" aria-hidden="true" />
        </span>
      </button>
      <button
        type="button"
        aria-label={t("nextPhoto")}
        disabled={activeIndex === listing.photos.length - 1}
        onClick={() => {
          showPhoto(activeIndex + 1);
        }}
        className="absolute end-1 top-1/2 z-20 inline-flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-0"
      >
        <span className="grid size-8 place-items-center rounded-full border border-border-subtle bg-surface/95 shadow-sm backdrop-blur-sm">
          <ChevronRight className="size-5 rtl:rotate-180" aria-hidden="true" />
        </span>
      </button>
      <PhotoPosition count={listing.photos.length} current={activeIndex + 1} />
    </div>
  );
}

function PhotoPosition({
  count,
  current,
}: {
  readonly count: number;
  readonly current?: number;
}) {
  const t = useTranslations("Marketplace");
  return (
    <span className="absolute bottom-2 start-2 z-20 inline-flex min-h-8 items-center gap-1.5 rounded-md bg-action-primary/80 px-2 text-body-small font-bold text-action-primary-ink backdrop-blur-sm">
      <Images className="size-4" aria-hidden="true" />
      {current === undefined
        ? t("photoCount", { count })
        : t("photoPosition", { current, maximum: count })}
    </span>
  );
}

function ListingPhoto({
  listing,
  photo = listing.photos[0],
  alt = listing.horseName,
  showBackdrop = false,
  className,
}: {
  readonly listing: MarketplaceListingPresentation;
  readonly photo?: MarketplaceListingPresentation["photos"][number];
  readonly alt?: string;
  readonly showBackdrop?: boolean;
  readonly className: string;
}) {
  if (photo === undefined)
    return (
      <div className={`${className} w-full bg-subtle`} aria-hidden="true" />
    );
  if ("position" in photo && photo.position !== undefined) {
    return (
      <div
        className={`${className} relative flex w-full items-center justify-center overflow-hidden bg-subtle`}
      >
        {showBackdrop ? (
          <div
            aria-hidden="true"
            className="absolute inset-0 scale-110 bg-cover bg-no-repeat opacity-40 blur-md"
            style={{
              backgroundImage: `url("${photo.url}")`,
              backgroundSize: "300% 100%",
              backgroundPosition:
                photo.position === "start"
                  ? "0% 50%"
                  : photo.position === "end"
                    ? "100% 50%"
                    : "50% 50%",
            }}
          />
        ) : null}
        <div
          role="img"
          aria-label={alt}
          className="relative aspect-[60/77] h-full shrink-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url("${photo.url}")`,
            backgroundSize: "300% 100%",
            backgroundPosition:
              photo.position === "start"
                ? "0% 50%"
                : photo.position === "end"
                  ? "100% 50%"
                  : "50% 50%",
          }}
        />
      </div>
    );
  }
  return (
    <div
      className={`${className} relative flex w-full items-center justify-center overflow-hidden bg-subtle`}
    >
      {showBackdrop ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 scale-110 bg-cover bg-center opacity-40 blur-md"
          style={{ backgroundImage: `url("${photo.url}")` }}
        />
      ) : null}
      {/* HISANI-QA: Next image optimization cannot fetch expiring server-authorized URLs reliably. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={photo.url}
        alt={alt}
        draggable={false}
        className="relative h-full w-full object-contain"
      />
    </div>
  );
}

function DetailTerm({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wider text-tertiary">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-semibold">{value}</dd>
    </div>
  );
}
